import React, { useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import GroupsIcon from '@mui/icons-material/Groups'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import moment from 'moment'
import API from '../../services/API'
import AnDateField from '../../components/DateTime/DateField'
import { DataGrid } from '@mui/x-data-grid'

export default function ReferralCodesDashboard() {
  const [codes, setCodes] = useState([])
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)

  const [form, setForm] = useState({
    owner: null,
    code: '',
    expiresAt: null,
  })

  const loadData = async () => {
    setLoading(true)

    try {
      const [codesRes, tenantsRes] = await Promise.all([
        API.get('/admincrm/referral-codes'),
        API.get('/admincrm/tenants-list'),
      ])

      console.log('codesRed', codesRes)
      setCodes(codesRes.data)
      setTenants(tenantsRes.data)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const createReferralCode = async () => {
    await API.post('/admincrm/referral-codes', {
      owner: form.owner?._id,
      code: form.code,
      expiresAt: form.expiresAt,
    })

    setDialogOpen(false)
    setForm({
      owner: null,
      code: '',
      expiresAt: null,
    })

    loadData()
  }

  const toggleActive = async (row) => {
    await API.patch(`/admincrm/referral-codes/${row._id}`, {
      isActive: !row.isActive,
    })

    loadData()
  }

  const copyReferralLink = async (code) => {
    const url = `https://ambunet.co.uk/signup?ref=${code}`
    await navigator.clipboard.writeText(url)
  }

  const columns = [
    {
      field: 'code',
      headerName: 'Code',
      flex: 1,
      minWidth: 160,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <LocalOfferIcon color="secondary" fontSize="small" />
          <Typography fontWeight={900}>{row.code}</Typography>
        </Stack>
      ),
    },
    {
      field: 'owner',
      headerName: 'Owner',
      flex: 1.4,
      minWidth: 220,
      valueGetter: (value, row) => row?.owner?.name ?? '',
      renderCell: ({ row }) => (
        <Stack spacing={0.1}>
          <Typography fontWeight={800} lineHeight={1.1}>
            {row.owner?.name ?? 'Unassigned'}
          </Typography>

          {row.owner?.subdomain && (
            <Typography variant="caption" color="text.secondary" lineHeight={1}>
              {row.owner.subdomain}.{process.env.ROOT_DOMAIN}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      field: 'usedBy',
      headerName: 'Uses',
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => row?.usedBy?.length ?? 0,
      renderCell: ({ row }) => (
        <Chip
          icon={<GroupsIcon />}
          label={row.usedBy?.length ?? 0}
          color={row.usedBy?.length ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'expiresAt',
      headerName: 'Expires',
      minWidth: 140,
      valueFormatter: (value) =>
        value ? moment(value).format('DD/MM/YYYY') : 'Never',
      renderCell: ({ row }) =>
        row.expiresAt ? (
          <Typography
            fontWeight={moment(row.expiresAt).isBefore(moment()) ? 800 : 400}
            color={
              moment(row.expiresAt).isBefore(moment()) ? 'error' : 'inherit'
            }
          >
            {moment(row.expiresAt).format('DD/MM/YYYY')}
          </Typography>
        ) : (
          <Typography color="text.secondary">Never</Typography>
        ),
    },
    {
      field: 'isActive',
      headerName: 'Active',
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Switch
          checked={!!row.isActive}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggleActive(row)}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 90,
      sortable: false,
      filterable: false,
      disableExport: true,
      align: 'center',
      renderCell: ({ row }) => (
        <Tooltip title="Copy referral signup link">
          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              copyReferralLink(row.code)
            }}
          >
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  return (
    <Box sx={{ p: 2 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 5,
          overflow: 'hidden',
          border: '1px solid #E2E8F0',
          boxShadow: '0 18px 60px rgba(36,47,64,0.12)',
        }}
      >
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            color: 'white',
            background:
              'linear-gradient(135deg, #1A2230 0%, #242F40 52%, #239AA4 150%)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.72 }}>
                AmbuNet CRM
              </Typography>

              <Typography variant="h4" fontWeight={900} color="white">
                Referral Codes
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.78)', maxWidth: 720 }}
              >
                Manage admin-issued referral codes and founding partner signup
                links. Codes record which tenant a signup came from — there is
                no trial to extend.
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              sx={{
                alignSelf: { xs: 'flex-start', md: 'center' },
                textTransform: 'none',
                fontWeight: 900,
                bgcolor: '#239AA4',
                '&:hover': { bgcolor: '#1D858E' },
              }}
            >
              Create referral code
            </Button>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <DataGrid
            rows={codes}
            columns={columns}
            loading={loading}
            getRowId={(row) => row?._id}
            autoHeight
            rowHeight={72}
            columnHeaderHeight={56}
            disableRowSelectionOnClick
            sx={{
              border: '1px solid #E2E8F0',
              borderRadius: 3,
              overflow: 'hidden',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#F8FAFC',
                color: '#64748B',
                fontWeight: 900,
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
                borderColor: '#EEF2F7',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#F8FAFC',
              },
            }}
          />
        </Box>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create referral code</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={tenants}
              value={form.owner}
              onChange={(e, newValue) =>
                setForm((prev) => ({ ...prev, owner: newValue }))
              }
              getOptionLabel={(option) => option?.name ?? ''}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              renderInput={(params) => (
                <TextField {...params} label="Owner tenant" fullWidth />
              )}
            />

            <TextField
              label="Referral Code"
              value={form.code}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  code: e.target.value.toUpperCase(),
                }))
              }
              placeholder="e.g. MEDIREK"
              fullWidth
              required
            />

            <AnDateField
              label="Expiry date"
              value={form.expiresAt}
              onChange={(date) =>
                setForm((prev) => ({ ...prev, expiresAt: date }))
              }
              fullWidth
            />

            <Alert severity="info">
              Signups entering this code are recorded against the owner tenant.
              It grants no trial — AmbuNet no longer offers trials.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>

          <Button
            variant="contained"
            disabled={!form.code}
            onClick={createReferralCode}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
